import { useEffect, useState } from "react";
import api, { fmtDate, apiError } from "@/lib/api";
import { SectionTitle, StatusBadge } from "@/components/common";
import { Modal, Field, Select, Row } from "@/pages/Notizie";
import { useFormErrors, V, errSummary } from "@/lib/formErrors";
import { toast } from "sonner";
import { Footprints, MessageSquarePlus, CheckCircle2 } from "lucide-react";

const VISITA_STATI = ["programmata","confermata","effettuata","annullata","no_show"];

export default function Visite() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fb, setFb] = useState(null);

  const load = () => api.get("/visite").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const setStato = async (v, stato) => {
    await api.put(`/visite/${v.id}`, { stato });
    toast.success(stato === "effettuata" ? "Visita effettuata — inserisci il feedback!" : "Aggiornata");
    load();
  };

  return (
    <div className="fade-up">
      <SectionTitle sub="Ogni visita effettuata richiede un feedback: il metodo lo impone.">Visite & Feedback</SectionTitle>

      {loading ? <div className="space-y-3">{[...Array(3)].map((_,i)=><div key={i} className="skeleton h-20 rounded-2xl"/>)}</div> :
      items.length === 0 ? <div className="card-immo p-12 text-center"><Footprints className="mx-auto text-[var(--muted)] mb-3" size={32}/><p className="text-[var(--muted)] text-sm">Nessuna visita programmata.</p></div> :
      <div className="space-y-3">
        {items.map((v) => (
          <div key={v.id} data-testid={`visita-${v.id}`} className="card-immo p-4 flex items-center gap-4 flex-wrap">
            <div className="w-10 h-10 rounded-xl bg-[#FC82ED]/10 text-[#FC82ED] flex items-center justify-center shrink-0"><Footprints size={18}/></div>
            <div className="flex-1 min-w-[160px]">
              <div className="font-semibold text-sm">{v.immobile_titolo || "Immobile"}</div>
              <div className="text-xs text-[var(--muted)]">{v.acquirente_nome || "Acquirente"} · {fmtDate(v.data)}</div>
            </div>
            <StatusBadge value={v.stato} />
            <div className="flex items-center gap-2">
              <Select testid={`visita-stato-${v.id}`} label="" value={v.stato} onChange={(e) => setStato(v, e.target.value)} opts={VISITA_STATI} />
              {v.stato === "effettuata" && !v.feedback_inserito && (
                <button data-testid={`feedback-btn-${v.id}`} onClick={() => setFb(v)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold whitespace-nowrap glow-pink"><MessageSquarePlus size={14}/> Feedback</button>
              )}
              {v.feedback_inserito && <span className="flex items-center gap-1 text-emerald-400 text-xs font-medium"><CheckCircle2 size={14}/> Feedback ok</span>}
            </div>
          </div>
        ))}
      </div>}

      {fb && <FeedbackModal visita={fb} onClose={() => setFb(null)} onSaved={() => { setFb(null); load(); }} />}
    </div>
  );
}

function FeedbackModal({ visita, onClose, onSaved }) {
  const [f, setF] = useState({ classificazione: "interessato", probabilita_proposta: 50 });
  const [saving, setSaving] = useState(false);
  const { errors, clear, validate, applyServer, summary } = useFormErrors();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clear(k); };
  const save = async () => {
    const errs = validate({ probabilita_proposta: [V.nonNegative] }, f);
    if (Object.keys(errs).length) return toast.error(errSummary(Object.keys(errs).length));
    setSaving(true);
    try { await api.post(`/visite/${visita.id}/feedback`, { ...f, probabilita_proposta: Number(f.probabilita_proposta)||0 }); toast.success("Feedback registrato"); onSaved(); }
    catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Feedback Visita" onClose={onClose} onSave={save} saving={saving} testid="feedback-modal" summary={summary}>
      <Select label="Classificazione" value={f.classificazione} testid="fb-class" error={errors.classificazione} onChange={set("classificazione")} opts={[["molto_interessato","Molto interessato"],["interessato","Interessato"],["da_valutare","Da valutare"],["non_interessato","Non interessato"]]} />
      <Row><Field label="Interesse prezzo" testid="fb-prezzo" error={errors.prezzo} value={f.prezzo || ""} onChange={set("prezzo")}/><Field label="Prob. proposta %" type="number" testid="fb-prob" error={errors.probabilita_proposta} value={f.probabilita_proposta} onChange={set("probabilita_proposta")}/></Row>
      <Field label="Aspetti positivi" testid="fb-pos" error={errors.positivi} value={f.positivi || ""} onChange={set("positivi")}/>
      <Field label="Aspetti negativi" testid="fb-neg" error={errors.negativi} value={f.negativi || ""} onChange={set("negativi")}/>
      <Field label="Prossimo follow-up" testid="fb-follow" error={errors.follow_up} value={f.follow_up || ""} onChange={set("follow_up")}/>
    </Modal>
  );
}
